import { hasPermissions } from '@common/utils/validation/authValidators'
import { TokenPermission } from '@data/rbac/model/rbac'

// Deliberately free of React and API imports so the rules can be unit-tested directly.

export const READ_INVITE_PERMISSION = 'READ:USER:INVITE'
export const EDIT_INVITE_PERMISSION = 'CREATE:USER:INVITE'
export const SMARTFLOW_ADMIN_PERMISSION = 'ACCOUNT:ADMIN:SMARTFLOW'

// Narrowed to the one field the decision needs, so callers and tests need not build a User.
export interface InvitationAccessUser {
  adminAccountIds?: string[]
}

export interface InvitationAccess {
  // Whether the Invitations tab renders at all.
  showTab: boolean
  // Whether the invitation list can be fetched and displayed.
  canRead: boolean
  // Whether edit, resend and deactivate are live rather than disabled.
  canEdit: boolean
  isAccountAdmin: boolean
  isSmartflowAdmin: boolean
  hasReadPermission: boolean
  hasEditPermission: boolean
}

// Reading invitations for an account needs BOTH halves: admin rights on that account, and
// the READ:USER:INVITE privilege that the Elevated Users group carries. Holding one without
// the other is the case this module exists to describe, because the tab used to disappear
// with nothing to explain why.
//
// ACCOUNT:ADMIN:SMARTFLOW is the exception. It is the internal-staff scope, not an
// account-scoped one, so it grants the read on its own — a Smartflow admin is not an admin of
// each customer account and never would be.
export const resolveInvitationAccess = (
  permissions: TokenPermission[],
  user: InvitationAccessUser | undefined,
  accountId: string,
): InvitationAccess => {
  const isAccountAdmin = !!user?.adminAccountIds?.includes(accountId)
  const isSmartflowAdmin = hasPermissions(permissions, [SMARTFLOW_ADMIN_PERMISSION])
  const hasReadPermission = hasPermissions(permissions, [READ_INVITE_PERMISSION])
  const hasEditPermission = hasPermissions(permissions, [EDIT_INVITE_PERMISSION])
  const canRead = isSmartflowAdmin || (isAccountAdmin && hasReadPermission)

  return {
    // Half-privileged users get the tab so the warning naming the missing half has somewhere
    // to live. A user with neither half sees no tab, exactly as before.
    showTab: canRead || isAccountAdmin || hasReadPermission,
    canRead,
    // Every invitation action POSTs against an endpoint scoped to CREATE:USER:INVITE, and
    // none of them are reachable without read access in the first place.
    canEdit: canRead && hasEditPermission,
    isAccountAdmin,
    isSmartflowAdmin,
    hasReadPermission,
    hasEditPermission,
  }
}
